import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import {
  PencilIcon, PlusIcon,
  ClipboardDocumentListIcon, XMarkIcon, PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const REQ_BANNER = (
  <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:10,padding:'10px 14px',fontSize:'.76rem',color:'#c2410c',marginBottom:'1.2rem',display:'flex',alignItems:'center',gap:8}}>
    📋 Add and edit actions require admin approval before taking effect.
  </div>
);

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .cls-root{font-family:'Sora',sans-serif;color:#1a1d2e;}
  .cls-page-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.75rem;flex-wrap:wrap;gap:.75rem;}
  .cls-page-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .cls-page-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .btn{padding:8px 18px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:transform .15s,box-shadow .15s;}
  .btn:active{transform:scale(.97);}
  .btn svg{width:15px;height:15px;stroke-width:2;flex-shrink:0;}
  .btn-orange{background:#f97316;color:white;} .btn-orange:hover{box-shadow:0 4px 14px rgba(249,115,22,.35);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;} .btn-ghost:hover{background:#e8ebf4;}
  .cls-panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;margin-bottom:1.25rem;}
  .cls-panel-header{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid #f0f2f8;flex-wrap:wrap;gap:8px;}
  .cls-panel-header-left{display:flex;align-items:center;gap:10px;}
  .cls-panel-icon{width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;background:#eef2ff;color:#3b6ef8;flex-shrink:0;}
  .cls-panel-icon svg{width:17px;height:17px;stroke-width:1.8;}
  .cls-panel-title{font-size:.88rem;font-weight:600;color:#1a1d2e;}
  .cls-panel-sub{font-size:.72rem;color:#9094a8;margin-top:1px;}
  .cls-table{width:100%;border-collapse:collapse;font-size:.8rem;}
  .cls-table thead tr{background:#f8f9fc;border-bottom:1px solid #eaecf4;}
  .cls-table th{padding:11px 16px;text-align:center !important;font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
  .cls-table th:last-child{text-align:right;}
  .cls-table tbody tr{border-bottom:1px solid #f3f5fb;transition:background .12s;}
  .cls-table tbody tr:last-child{border-bottom:none;} .cls-table tbody tr:hover{background:#fafbff;}
  .cls-table td{ padding:13px 16px;color:#3a3f58;font-size:.8rem;vertical-align:middle; text-align:center !important; }
  .cls-table td:last-child{text-align:right;}
  .cls-name-cell{display:flex;align-items:center;gap:10px;}
  .cls-avatar{width:32px;height:32px;border-radius:9px;background:linear-gradient(135deg,#3b6ef8,#8b5cf6);display:flex;align-items:center;justify-content:center;font-size:.72rem;font-weight:700;color:white;flex-shrink:0;}
  .cls-name-main{font-weight:600;color:#1a1d2e;font-size:.83rem;}
  .tag-wrap{display:flex;flex-wrap:wrap;gap:4px;}
  .tag{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:99px;font-size:.68rem;font-weight:600;background:#eef2ff;color:#3b6ef8;white-space:nowrap;}
  .tag.green{background:#ecfdf5;color:#10c98e;}
  .tag-remove{cursor:pointer;opacity:.6;display:inline-flex;align-items:center;}
  .tag-remove:hover{opacity:1;}
  .tag-remove svg{width:10px;height:10px;stroke-width:2.5;}
  .fee-val{font-family:'JetBrains Mono',monospace;font-size:.8rem;color:#1a1d2e;font-weight:500;}
  .action-btn{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:background .13s,border-color .13s;margin-left:6px;}
  .action-btn svg{width:14px;height:14px;stroke-width:1.8;}
  .action-btn.edit{color:#f97316;} .action-btn.edit:hover{background:#fff7ed;border-color:#f97316;}
  .action-btn.delete{color:#ef4444;} .action-btn.delete:hover{background:#fff1f1;border-color:#fecaca;}
  .cls-empty{text-align:center;padding:56px 20px;color:#b0b5cc;font-size:.82rem;}
  .cls-loading{text-align:center;padding:56px 20px;color:#9094a8;font-size:.82rem;display:flex;align-items:center;justify-content:center;gap:10px;}
  .spinner{width:16px;height:16px;border:2px solid #e4e7f0;border-top-color:#f97316;border-radius:50%;animation:spin .7s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg);}}
  .cls-cards{display:none;padding:12px;}
  .cls-card{background:#fafbff;border-radius:12px;border:1px solid #e8ebf4;padding:14px;margin-bottom:10px;}
  .cls-card-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;gap:8px;}
  .cls-card-name{display:flex;align-items:center;gap:10px;}
  .cls-card-actions{display:flex;gap:6px;}
  .cls-card-row{display:flex;justify-content:space-between;align-items:flex-start;font-size:.76rem;padding:5px 0;border-bottom:1px solid #f0f2f8;gap:8px;}
  .cls-card-row:last-child{border-bottom:none;}
  .cls-card-label{color:#9094a8;font-weight:600;flex-shrink:0;}
  .cls-card-val{text-align:right;}
  .modal-overlay{position:fixed;inset:0;background:rgba(10,12,28,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;animation:overlayIn .2s ease;}
  @keyframes overlayIn{from{opacity:0}to{opacity:1}}
  .modal-box{background:white;border-radius:20px;width:100%;max-height:90vh;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);animation:modalIn .25s ease;overflow:hidden;display:flex;flex-direction:column;}
  @keyframes modalIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
  .modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;}
  .modal-title{font-size:.95rem;font-weight:700;color:#0f1123;letter-spacing:-.02em;}
  .modal-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#9094a8;transition:background .13s;}
  .modal-close:hover{background:#f0f2f7;color:#1a1d2e;}
  .modal-close svg{width:14px;height:14px;stroke-width:2;}
  .modal-body{padding:22px;overflow-y:auto;}
  .modal-footer{display:flex;justify-content:flex-end;gap:8px;padding:16px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;flex-wrap:wrap;}
  .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .form-field{display:flex;flex-direction:column;gap:5px;}
  .form-field.full{grid-column:1/-1;}
  .form-label{font-size:.7rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .form-input{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 12px;border:1px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;transition:border-color .15s,background .15s;width:100%;}
  .form-input:focus{border-color:#f97316;background:white;box-shadow:0 0 0 3px rgba(249,115,22,.08);}
  .tag-input-wrap{display:flex;flex-wrap:wrap;gap:6px;padding:8px 10px;border:1px solid #e0e4f0;border-radius:10px;background:#f8f9fc;min-height:42px;align-items:center;cursor:text;}
  .tag-input-wrap:focus-within{border-color:#f97316;background:white;box-shadow:0 0 0 3px rgba(249,115,22,.08);}
  .tag-input-inner{border:none;outline:none;background:transparent;font-family:'Sora',sans-serif;font-size:.82rem;color:#1a1d2e;min-width:80px;flex:1;padding:2px 0;}
  .tag-add-btn{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:7px;background:#fff7ed;color:#f97316;border:none;font-family:'Sora',sans-serif;font-size:.72rem;font-weight:600;cursor:pointer;flex-shrink:0;}
  .tag-add-btn:hover{background:#fed7aa;}
  .tag-add-btn svg{width:11px;height:11px;stroke-width:2.5;}
  .cls-fade{animation:clsFadeUp .4s ease both;}
  @keyframes clsFadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @media(max-width:768px){
    .cls-page-title{font-size:1.2rem;} .cls-page-header{margin-bottom:1rem;}
    .cls-table-wrap{display:none!important;} .cls-cards{display:block;}
    .modal-body{padding:16px;} .modal-footer{justify-content:stretch;}
    .modal-footer .btn{flex:1;justify-content:center;}
    .form-grid{grid-template-columns:1fr;} .form-field.full{grid-column:1;}
  }
`;

const getInitials = (name = '') =>
  name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'C';

// ── Tag Input Component ───────────────────────────────────────────────────────
const TagInput = ({ tags, onChange, placeholder, color = 'orange' }) => {
  const [inputVal, setInputVal] = useState('');

  const addTag = () => {
    const val = inputVal.trim();
    if (!val) return;
    if (tags.includes(val)) { setInputVal(''); return; }
    onChange([...tags, val]);
    setInputVal('');
  };

  const removeTag = (t) => onChange(tags.filter(x => x !== t));

  const handleKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addTag(); }
    if (e.key === 'Backspace' && !inputVal && tags.length > 0) removeTag(tags[tags.length - 1]);
  };

  return (
    <div className="tag-input-wrap" onClick={e => e.currentTarget.querySelector('input')?.focus()}>
      {tags.map(t => (
        <span key={t} className={`tag ${color === 'green' ? 'green' : ''}`}>
          {t}
          <span className="tag-remove" onClick={() => removeTag(t)}><XMarkIcon /></span>
        </span>
      ))}
      <input
        className="tag-input-inner"
        value={inputVal}
        onChange={e => setInputVal(e.target.value)}
        onKeyDown={handleKey}
        placeholder={tags.length === 0 ? placeholder : ''}
      />
      <button type="button" className="tag-add-btn" onClick={addTag}><PlusIcon />Add</button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const CoordClasses = () => {
  const [classes,   setClasses]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null); // null = add, object = edit
  const [target,    setTarget]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [formData,  setFormData]  = useState({ name: '', sections: [], subjects: [], feeAmount: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/coordinator/classes');
      setClasses(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load classes'); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditing(null); setTarget(null);
    setFormData({ name: '', sections: [], subjects: [], feeAmount: '' });
    setShowModal(true);
  };

  const openEdit = (cls) => {
    setEditing(cls); setTarget(cls);
    setFormData({
      name:      cls.name,
      sections:  cls.sections  || [],
      subjects:  cls.subjects  || [],
      feeAmount: cls.feeAmount || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Add / Edit
    if (!formData.name.trim())          { toast.error('Class name is required');       return; }
    if (formData.sections.length === 0) { toast.error('Add at least one section');     return; }
    if (formData.subjects.length === 0) { toast.error('Add at least one subject');     return; }

    const payload = {
      ...(editing ? { classId: editing._id } : {}),
      name:      formData.name.trim(),
      sections:  formData.sections,
      subjects:  formData.subjects,
      feeAmount: formData.feeAmount,
    };

    try {
      setSaving(true);
      await api.post('/coordinator/requests', {
        type:    editing ? 'class_edit' : 'class_add',
        payload,
      });
      toast.success(editing
        ? 'Edit request submitted — awaiting admin approval'
        : 'Add request submitted — awaiting admin approval');
      setShowModal(false);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <style>{style}</style>
      <div className="cls-root cls-fade">

        <div className="cls-page-header">
          <div>
            <h1 className="cls-page-title">Classes</h1>
            <p className="cls-page-sub">View classes · submit requests to add, edit or remove</p>
          </div>
          <button className="btn btn-orange" onClick={openAdd}><PlusIcon />Add Class</button>
        </div>

        {REQ_BANNER}

        <div className="cls-panel">
          <div className="cls-panel-header">
            <div className="cls-panel-header-left">
              <div className="cls-panel-icon"><ClipboardDocumentListIcon /></div>
              <div>
                <div className="cls-panel-title">All Classes</div>
                <div className="cls-panel-sub">{classes.length} class{classes.length !== 1 ? 'es' : ''} registered</div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="cls-loading"><div className="spinner" />Loading classes…</div>
          ) : classes.length === 0 ? (
            <div className="cls-empty">No classes found.</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="cls-table-wrap" style={{ overflowX: 'auto' }}>
                <table className="cls-table">
                  <thead>
                    <tr>
                      <th>Class</th>
                      <th>Sections</th>
                      <th style={{ textAlign: 'center' }}>Subjects</th>
                      <th>Class Teacher</th>
                      <th style={{textAlign:'center'}}>Monthly Fee<div style={{fontSize:'.62rem',fontWeight:500,textTransform:'none',letterSpacing:'normal',color:'#9094a8'}}>(Students)</div></th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map(cls => (
                      <tr key={cls._id}>
                        <td>
                          <div className="cls-name-cell">
                            <div className="cls-avatar">{getInitials(cls.name)}</div>
                            <span className="cls-name-main">{cls.name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="tag-wrap">
                            {cls.sections?.map(s => <span key={s} className="tag">{s}</span>)}
                          </div>
                        </td>
                        <td>
                          <div className="tag-wrap" style={{ justifyContent: 'center' }}>
                            {cls.subjects?.map(s => <span key={s} className="tag green">{s}</span>)}
                          </div>
                        </td>
                        <td>
                          {cls.classTeacher?.user?.name
                            ? <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                <div style={{ width: 24, height: 24, borderRadius: 7, background: 'linear-gradient(135deg,#10c98e,#3b6ef8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.6rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
                                  {getInitials(cls.classTeacher.user.name)}
                                </div>
                                <span style={{ fontSize: '.8rem', color: '#3a3f58' }}>{cls.classTeacher.user.name}</span>
                              </div>
                            : <span style={{ color: '#c0c5dc', fontSize: '.78rem' }}>Not assigned</span>}
                        </td>
                        <td style={{textAlign:'center'}}><span className="fee-val">Rs. {cls.feeAmount?.toLocaleString()}</span></td>
                        <td>
                          <button className="action-btn edit" onClick={() => openEdit(cls)} title="Edit"><PencilIcon /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="cls-cards">
                {classes.map(cls => (
                  <div key={cls._id} className="cls-card">
                    <div className="cls-card-top">
                      <div className="cls-card-name">
                        <div className="cls-avatar">{getInitials(cls.name)}</div>
                        <span className="cls-name-main">{cls.name}</span>
                      </div>
                      <div className="cls-card-actions">
                        <button className="action-btn edit" onClick={() => openEdit(cls)}><PencilIcon /></button>
                      </div>
                    </div>
                    <div className="cls-card-row">
                      <span className="cls-card-label">Sections</span>
                      <div className="cls-card-val tag-wrap" style={{ justifyContent: 'flex-end' }}>
                        {cls.sections?.map(s => <span key={s} className="tag">{s}</span>)}
                      </div>
                    </div>
                    <div className="cls-card-row">
                      <span className="cls-card-label">Subjects</span>
                      <div className="cls-card-val tag-wrap" style={{ justifyContent: 'flex-end' }}>
                        {cls.subjects?.map(s => <span key={s} className="tag green">{s}</span>)}
                      </div>
                    </div>
                    <div className="cls-card-row">
                      <span className="cls-card-label">Class Teacher</span>
                      <span className="cls-card-val" style={{ fontSize: '.78rem', color: '#3a3f58' }}>
                        {cls.classTeacher?.user?.name || <span style={{ color: '#c0c5dc' }}>Not assigned</span>}
                      </span>
                    </div>
                    <div className="cls-card-row">
                      <span className="cls-card-label">Monthly Fee <span style={{textTransform:'none',fontWeight:400}}>(Students)</span></span>
                      <span className="cls-card-val fee-val">Rs. {cls.feeAmount?.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Modal ── */}
      {showModal && createPortal(
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <span className="modal-title">
                {editing ? 'Request: Edit Class' : 'Request: Add Class'}
              </span>
              <button className="modal-close" onClick={() => setShowModal(false)}><XMarkIcon /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                    <div className="form-field full">
                      <label className="form-label">Class Name *</label>
                      <input type="text" className="form-input" placeholder="e.g. Grade 9"
                        value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                    </div>

                    <div className="form-field full">
                      <label className="form-label">
                        Sections * <span style={{ fontWeight: 400, textTransform: 'none', fontSize: '.68rem', color: '#b0b5cc' }}>(type then click Add or press Enter)</span>
                      </label>
                      <TagInput
                        tags={formData.sections}
                        onChange={tags => setFormData({ ...formData, sections: tags })}
                        placeholder="e.g. A"
                        color="orange"
                      />
                    </div>

                    <div className="form-field full">
                      <label className="form-label">
                        Subjects * <span style={{ fontWeight: 400, textTransform: 'none', fontSize: '.68rem', color: '#b0b5cc' }}>(type then click Add or press Enter)</span>
                      </label>
                      <TagInput
                        tags={formData.subjects}
                        onChange={tags => setFormData({ ...formData, subjects: tags })}
                        placeholder="e.g. Mathematics"
                        color="green"
                      />
                    </div>

                    <div className="form-field full">
                      <label className="form-label">Monthly Fee (Rs.)</label>
                      <input type="number" className="form-input" placeholder="5000"
                        value={formData.feeAmount} onChange={e => setFormData({ ...formData, feeAmount: e.target.value })} />
                    </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>
                  <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}
    </>
  );
};

export default CoordClasses;